import React, { useEffect, useState } from 'react'
import { Timestamp } from 'firebase/firestore'
import { Link as RouterLink } from 'react-router-dom'
import {
  AccountBalanceWallet,
  People,
  PermIdentity,
  LocationOn,
  HourglassEmpty,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { GetTimeFromDate, HasWorkoutFinished } from '../hooks/timestamp'
import { Workout } from '../providers/workouts'
import { useAuth } from '../core/providers/auth'
import { firestore } from '../core/'
import { GetImage } from '../assets/workout-image-provider'
import { useWorkoutSettings } from '../providers/workout-settings'
import {
  BookingError,
  claimWorkoutWaitlistSpot,
  joinWorkoutWaitlist,
  leaveWorkoutWaitlist,
  toggleWorkoutBooking,
} from '../lib/booking'
import { features } from '../core/config/features'

export default function HomeWorkout({
  workout: {
    id,
    type,
    coaches,
    startTime,
    endTime,
    participantsCount,
    participantsList,
    numberOfPlaces,
    registrationTime,
    location,
    credits,
    waitlistCount = 0,
    waitlist = [],
  },
}: {
  workout: Workout
}) {
  const { user, admin, coach } = useAuth()
  const [loading, setLoading] = useState(false)
  const [inParticipantsList, setInParticipantsList] = useState(false)
  const [bookingError, setBookingError] = useState<string | null>(null)
  const { palette } = useTheme()
  const { getWorkoutType, getWorkoutTypeName, isWorkoutTypeBookable } =
    useWorkoutSettings()
  const workoutType = getWorkoutType(type)
  const isTypeBookable = isWorkoutTypeBookable(type)
  const typeColor = workoutType.color ?? palette.primary.main
  const typeName = getWorkoutTypeName(type)
  const workoutCredits = features.credits ? Number(credits ?? 0) : 0
  const userCredits = Number(user?.credits ?? 0)
  const hasEnoughCredits = !user || workoutCredits <= userCredits
  const waitlistPosition = user
    ? waitlist.findIndex((entry) => entry.uid === user.uid) + 1
    : 0
  const inWaitlist = waitlistPosition > 0
  const canSeeFullWaitlist = admin || coach
  const isRegistrationOpen =
    Timestamp.now() <
    Timestamp.fromMillis((startTime.seconds - registrationTime * 3600) * 1000)
  const spotsAvailable = participantsCount < numberOfPlaces
  const waitlistClosesAt = new Date(startTime.toDate().getTime() - 2 * 60 * 60 * 1000)
  const isWaitlistOpen = new Date() < waitlistClosesAt
  const canClaimWaitlistSpot =
    canSeeFullWaitlist && inWaitlist && spotsAvailable && waitlistPosition === 1
  const isBookable =
    isTypeBookable &&
    !inParticipantsList &&
    !inWaitlist &&
    spotsAvailable &&
    !IsWorkoutInProgress() &&
    !HasWorkoutFinished(endTime.toDate()) &&
    isRegistrationOpen &&
    hasEnoughCredits

  const handleCheck = async () => {
    setLoading(true)
    setBookingError(null)

    try {
      await toggleWorkoutBooking(firestore, id, user, {
        creditsEnabled: features.credits,
        participantsCount,
        numberOfPlaces,
      })
    } catch (error) {
      console.log(error)
      setBookingError(
        error instanceof BookingError
          ? error.message
          : 'Det gick inte att uppdatera bokningen. Försök igen om en stund.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleWaitlist = async () => {
    setLoading(true)
    setBookingError(null)

    try {
      if (inWaitlist) {
        await leaveWorkoutWaitlist(firestore, id, user)
      } else {
        await joinWorkoutWaitlist(firestore, id, user, {
          creditsEnabled: features.credits,
        })
      }
    } catch (error) {
      console.log(error)
      setBookingError(
        error instanceof BookingError
          ? error.message
          : 'Det gick inte att uppdatera kön. Försök igen om en stund.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleClaimWaitlistSpot = async () => {
    setLoading(true)
    setBookingError(null)

    try {
      await claimWorkoutWaitlistSpot(firestore, id, user, {
        creditsEnabled: features.credits,
        participantsCount,
        numberOfPlaces,
        waitlistPosition,
      })
    } catch (error) {
      console.log(error)
      setBookingError(
        error instanceof BookingError
          ? error.message
          : 'Det gick inte att boka den lediga platsen. Försök igen om en stund.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handlePanelAction = () => {
    if (!isTypeBookable) {
      return
    }

    if (canClaimWaitlistSpot) {
      handleClaimWaitlistSpot()
      return
    }

    if (inWaitlist || (!spotsAvailable && isWaitlistOpen && hasEnoughCredits)) {
      handleWaitlist()
      return
    }

    if (isBookable) {
      handleCheck()
    }
  }

  const panelIsClickable =
    isTypeBookable &&
    (isBookable ||
      canClaimWaitlistSpot ||
      inWaitlist ||
      (!spotsAvailable && isWaitlistOpen && hasEnoughCredits))

  function IsWorkoutInProgress() {
    const now = new Date()
    return now <= endTime.toDate() && now >= startTime.toDate()
  }

  function getStartTime() {
    let s = GetTimeFromDate(startTime.toDate())
    const startSplit = s.split(':')
    const zero = '0'

    if (startSplit[0].length === 1) startSplit[0] = zero.concat(startSplit[0])
    if (startSplit[1].length === 1) startSplit[1] = zero.concat(startSplit[1])

    return `${startSplit[0]}:${startSplit[1]}`
  }

  function getLength() {
    var diff =
      (endTime.toDate().getTime() - startTime.toDate().getTime()) / 1000
    diff /= 60
    return Math.abs(Math.round(diff))
  }

  useEffect(() => {
    const arr = participantsList.map(({ uid }) => uid)
    user && arr.includes(user.uid)
      ? setInParticipantsList(true)
      : setInParticipantsList(false)
    setLoading(false)
    // eslint-disable-next-line
  }, [participantsList, user?.uid])

  return (
    <Card
      sx={{
        display: 'flex',
        borderLeft: '6px solid',
        borderLeftColor: typeColor,
        position: 'relative',
      }}
    >
      <CardActionArea component={RouterLink} to={`/workouts/${id}`}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <CardHeader
            title={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: typeColor,
                    boxShadow: `0 0 0 4px ${alpha(typeColor, 0.14)}`,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                  {typeName.toUpperCase()}
                </Typography>
                {!isTypeBookable && (
                  <Chip
                    size="small"
                    label="Abonnerat"
                    sx={{
                      backgroundColor: alpha(typeColor, 0.12),
                      color: typeColor,
                      fontWeight: 700,
                    }}
                  />
                )}
                {isTypeBookable && workoutCredits > 0 && (
                  <Chip
                    size="small"
                    label={`${workoutCredits} credits`}
                    sx={{
                      backgroundColor: hasEnoughCredits
                        ? alpha(typeColor, 0.12)
                        : alpha(palette.error.main, 0.12),
                      color: hasEnoughCredits ? typeColor : palette.error.main,
                      fontWeight: 700,
                    }}
                  />
                )}
                {isTypeBookable && waitlistCount > 0 && (
                  <Chip
                    size="small"
                    label={
                      inWaitlist && canSeeFullWaitlist
                        ? `Köplats ${waitlistPosition}`
                        : inWaitlist
                        ? 'I kö'
                        : `Kö ${waitlistCount}`
                    }
                    sx={{
                      backgroundColor: alpha(typeColor, 0.12),
                      color: typeColor,
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
            }
          />
          <CardContent sx={{ paddingTop: 0, paddingBottom: '1.2rem' }}>
            <Box sx={{ width: '100%', display: 'flex', flexGrow: 1 }}>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ margin: 0 }}>
                  <ListItemButton sx={{ padding: 0 }}>
                    <ListItemIcon sx={{ minWidth: '2rem' }}>
                      <AccessTimeIcon sx={{ fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      sx={{ fontSize: '.8rem' }}
                      disableTypography
                      primary={
                        isBookable
                          ? `${getLength()} min`
                          : `Kl ${getStartTime()}, ${getLength()} min`
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {isTypeBookable && (
                  <ListItem disablePadding>
                    <ListItemButton sx={{ padding: 0 }}>
                      <ListItemIcon sx={{ minWidth: '2rem' }}>
                        <People sx={{ fontSize: '1.2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ fontSize: '.8rem', padding: 0 }}
                        disableTypography
                        primary={`${participantsCount}/${numberOfPlaces}`}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                {isTypeBookable && workoutCredits > 0 && (
                  <ListItem disablePadding>
                    <ListItemButton sx={{ padding: 0 }}>
                      <ListItemIcon sx={{ minWidth: '2rem' }}>
                        <AccountBalanceWallet sx={{ fontSize: '1.2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ fontSize: '.8rem', padding: 0 }}
                        disableTypography
                        primary={
                          user
                            ? `Saldo ${userCredits} credits`
                            : `${workoutCredits} credits`
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                {isTypeBookable && waitlistCount > 0 && (
                  <ListItem disablePadding>
                    <ListItemButton sx={{ padding: 0 }}>
                      <ListItemIcon sx={{ minWidth: '2rem' }}>
                        <HourglassEmpty sx={{ fontSize: '1.2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ fontSize: '.8rem', padding: 0 }}
                        disableTypography
                        primary={
                          inWaitlist
                            ? canSeeFullWaitlist
                              ? `Köplats ${waitlistPosition} av ${waitlistCount}`
                              : 'Du står i kön'
                            : `${waitlistCount} i kö`
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                <ListItem disablePadding>
                  <ListItemButton sx={{ padding: 0 }}>
                    <ListItemIcon sx={{ minWidth: '2rem' }}>
                      <PermIdentity sx={{ fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        coaches?.length > 0 ? (
                          <>
                            {coaches.map((c, i) => (
                              <span key={i}>
                                {c.displayName}
                                {i + 1 !== coaches.length && ','}{' '}
                              </span>
                            ))}
                          </>
                        ) : (
                          'Ej angivet'
                        )
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton sx={{ padding: 0 }}>
                    <ListItemIcon sx={{ minWidth: '2rem' }}>
                      <LocationOn sx={{ fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText primary={location} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          </CardContent>
        </Box>
      </CardActionArea>
      <Box
        sx={{
          backgroundColor: typeColor,
          display: 'flex',
          alignItems: 'flex-end',
        }}
      ></Box>
      
        <Box
          sx={{
            backgroundImage: `url(${workoutType.image ?? GetImage(type)})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            maxWidth: '6.5rem',
            minWidth: '6.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {' '}
          {isBookable && !HasWorkoutFinished(endTime.toDate()) && !loading && (
            <Button
              disableRipple
              onClick={() => handleCheck() }
              variant="contained"
              sx={{
                fontSize: '1.5rem',
                display: 'flex',
                justifyContent: 'center',
                alignSelf: 'center',
                margin: '0 auto',
                width: '6.5rem',
                height: '2.5rem',
                backgroundColor: typeColor,
                '&:hover': {
                  backgroundColor: typeColor,
                  filter: 'brightness(0.92)',
                },
              }}
            >
              {getStartTime()}
            </Button>
          )}
          { loading ? <CircularProgress/> : isRegistrationOpen ? (
            <Box
              sx={{
	                backgroundColor: inParticipantsList
	                  ? typeColor
	                  : spotsAvailable
	                  ? 'transparent'
	                  : typeColor,
                display: 'flex',
                flexGrow: 1,
                width: '6.5rem',
                opacity: inParticipantsList
                  ? '85%'
                  : spotsAvailable
                  ? '100%'
                  : '85%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
              onClick={handlePanelAction}
                sx={{
                  fontSize: !isTypeBookable
                    ? '1.2rem'
                    : panelIsClickable
                    ? '2.2rem'
                    : '1.8rem',
                  fontWeight: 'bold',
                  color: inParticipantsList
	                    ? palette.primary.contrastText
	                    : spotsAvailable
                    ? 'white'
                    : palette.primary.contrastText,
                  transform: 'rotate(-90deg)',
                  cursor: panelIsClickable ? 'pointer' : 'default'
                }}
              >
	                  {inParticipantsList
	                  ? 'BOKAD'
                    : canClaimWaitlistSpot
                    ? 'PLATS'
                    : inWaitlist
                    ? 'KÖ'
                    : !isTypeBookable
                    ? 'ABONNERAT'
                    : !hasEnoughCredits
                    ? 'CREDITS'
	                  : spotsAvailable
	                  ? IsWorkoutInProgress()
	                    ? ''
	                    : 'BOKA'
                  : isWaitlistOpen
                  ? 'KÖA'
                  : 'FULLBOKAD'}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: typeColor,
                display: 'flex',
                flexGrow: 1,
                maxWidth: '6.5rem',
                opacity: inParticipantsList
                  ? '85%'
                  : spotsAvailable
                  ? IsWorkoutInProgress() && isRegistrationOpen
                    ? '50'
                    : '85%'
                  : '85%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  fontSize: '1.8rem',
                  fontWeight: 'bold',
                  color: palette.primary.contrastText,
                  transform: 'rotate(-90deg)',
                }}
              >
                { HasWorkoutFinished(endTime.toDate())? 'AVSLUTAD' : IsWorkoutInProgress()? 'PÅGÅENDE' : 'STÄNGT'}
              </Box>
            </Box>
          )}
        </Box>
      <Snackbar
        open={!!bookingError}
        autoHideDuration={6000}
        onClose={() => setBookingError(null)}
      >
        <Alert severity="error" onClose={() => setBookingError(null)}>
          {bookingError}
        </Alert>
      </Snackbar>
    </Card>
  )
}
